import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CurrentUser } from '../../types/user';
import type {
  ReportingBreakdownFilters,
  ReportingDimension,
  ReportingFilters,
  ReportingSourceLevel,
} from '../../types/reporting';
import { patchMyPreferences } from '../accounts';
import { getReportingBreakdown } from './api';
import {
  BREAKDOWN_COLUMNS,
  DIMENSION_LABEL,
  DIMENSION_ORDER,
  canonicalizeGroupBy,
  isStatKey,
  type BreakdownColumnKey,
  type BreakdownStatKey,
} from './breakdownColumns';
import {
  renderBreakdownAttributeCell,
  renderBreakdownDimCell,
  renderBreakdownStatCell,
  type MoneyDisplay,
  StatusDotLegend,
} from './breakdownCell';
import {
  DEFAULT_ROLLUP_SORT,
  buildRollup,
  type RollupSort,
  type RollupSortKey,
  type SeparatorTier,
} from './breakdownGrouping';
import { BreakdownTree } from './BreakdownTree';
import { buildBreakdownCsv, breakdownCsvFilename } from './breakdownCsv';
import { ExplorerViewMenu } from './ExplorerViewMenu';
import {
  DEFAULT_EXPLORER_STATE,
  REPORTING_EXPLORER_GRID_KEY,
  decodeExplorerState,
  encodeExplorerState,
  type ExplorerMode,
  type ExplorerState,
} from './explorerPrefs';

// Human note about which stored grain the numbers came from (§3a source-level).
const SOURCE_LEVEL_NOTE: Record<ReportingSourceLevel, string> = {
  campaign: 'rolled up from campaign-level data (full history)',
  adset: 'rolled up from ad-set-level data (ad-grain dates only)',
  ad: 'rolled up from ad-level data (ad-grain dates only)',
};

function separatorClass(tier: SeparatorTier): string {
  switch (tier) {
    case 'strong':
      return 'border-t-2 border-slate-300';
    case 'hairline':
      return 'border-t border-slate-100';
    case 'none':
      return '';
  }
}

// Trigger a client-side CSV download from a string (mirrors CampaignTable).
function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// The drill-down explorer (Phase 3a finish): group-by chips + Flat/Tree mode + the
// subtotal rollup pivot, off the /breakdown endpoint. Configurable stat columns,
// hierarchy-preserving sort, account/Meta links, and CSV export. Tree is a stub
// until 3b (which reuses buildRollup). Chip/mode/column state persists via
// gridPreferences.
export function BreakdownExplorer({
  filters,
  user,
  money,
}: {
  filters: ReportingFilters;
  user: CurrentUser;
  /** The agency display currency for this page. Absent = native. */
  money?: MoneyDisplay;
}) {
  const qc = useQueryClient();
  const [state, setState] = useState<ExplorerState>(() =>
    decodeExplorerState(user.gridPreferences?.[REPORTING_EXPLORER_GRID_KEY]),
  );
  const [sort, setSort] = useState<RollupSort>(DEFAULT_ROLLUP_SORT);

  const saveMut = useMutation({
    mutationFn: (next: ExplorerState) =>
      patchMyPreferences(REPORTING_EXPLORER_GRID_KEY, encodeExplorerState(next)),
    onSuccess: (u) => qc.setQueryData(['me'], u),
  });

  const commit = (next: ExplorerState) => {
    setState(next);
    saveMut.mutate(next, {
      onError: (err) => console.error('Failed to save explorer preferences', err),
    });
  };

  const toggleDim = (dim: ReportingDimension) => {
    const has = state.groupBy.includes(dim);
    if (has && state.groupBy.length === 1) return; // keep at least one dimension
    const nextGroupBy = canonicalizeGroupBy(
      has ? state.groupBy.filter((d) => d !== dim) : [...state.groupBy, dim],
    );
    commit({ ...state, groupBy: nextGroupBy });
  };

  const setMode = (mode: ExplorerMode) => {
    if (mode !== state.mode) commit({ ...state, mode });
  };

  const setColumns = (statColumns: BreakdownColumnKey[]) => commit({ ...state, statColumns });

  const groupBy = state.groupBy.length > 0 ? state.groupBy : DEFAULT_EXPLORER_STATE.groupBy;
  const visibleCols = useMemo(
    () => BREAKDOWN_COLUMNS.filter((c) => state.statColumns.includes(c.key)),
    [state.statColumns],
  );
  // The status dot now rides the campaign/ad-set/ad NAME (not a column), so show
  // the legend whenever the grouping surfaces one of those levels.
  const showStatusLegend = groupBy.some((d) => d !== 'account');

  const breakdownFilters = useMemo<ReportingBreakdownFilters>(
    () => ({ ...filters, groupBy }),
    [filters, groupBy],
  );

  const rangeValid = Boolean(filters.from && filters.to && filters.from <= filters.to);
  const query = useQuery({
    queryKey: ['reporting-breakdown', breakdownFilters],
    queryFn: () => getReportingBreakdown(breakdownFilters),
    // Tree is a stub in 3a — only the flat pivot fetches (3b drives the tree).
    enabled: rangeValid && state.mode === 'flat',
    placeholderData: (prev) => prev,
  });

  const leafRows = query.data?.rows ?? [];
  const sourceLevel = query.data?.sourceLevel;
  const needsAdGrain = groupBy.includes('adset') || groupBy.includes('ad');

  const rollup = useMemo(() => buildRollup(leafRows, groupBy, sort), [leafRows, groupBy, sort]);

  const onSortDim = () =>
    setSort((s) => (s.key === 'name' ? { key: 'name', dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: 'name', dir: 'asc' }));
  const onSortStat = (key: BreakdownStatKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

  const sortArrow = (key: RollupSortKey) =>
    sort.key === key ? (sort.dir === 'asc' ? '▲' : '▼') : '';

  const onExport = () => {
    if (rollup.length === 0) return;
    downloadCsv(
      breakdownCsvFilename(filters.from, filters.to),
      buildBreakdownCsv(rollup, groupBy, state.statColumns),
    );
  };

  const totalColumns = groupBy.length + visibleCols.length;

  return (
    <section className="mt-8">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xs font-medium uppercase tracking-wide text-slate-400">explorer</h2>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded border border-slate-300" role="group" aria-label="View mode">
            <button
              type="button"
              onClick={() => setMode('flat')}
              aria-pressed={state.mode === 'flat'}
              className={`px-2 py-1 text-sm ${state.mode === 'flat' ? 'bg-slate-100 font-medium text-slate-800' : 'text-slate-500 hover:bg-slate-50'}`}
            >
              Flat
            </button>
            <span className="w-px bg-slate-300" aria-hidden />
            <button
              type="button"
              onClick={() => setMode('tree')}
              aria-pressed={state.mode === 'tree'}
              className={`px-2 py-1 text-sm ${state.mode === 'tree' ? 'bg-slate-100 font-medium text-slate-800' : 'text-slate-500 hover:bg-slate-50'}`}
            >
              Tree
            </button>
          </div>
          {/* CSV exports the flat rollup; the Tree is lazily fetched (no full
              dataset in hand), so Export is a Flat-only action. */}
          {state.mode === 'flat' && (
            <button
              type="button"
              onClick={onExport}
              disabled={rollup.length === 0}
              className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-40"
              title="Export the displayed view to CSV"
            >
              Export CSV
            </button>
          )}
          <ExplorerViewMenu visible={state.statColumns} onChange={setColumns} />
        </div>
      </div>

      {/* Group-by chips — a Flat-only concept (Tree always drills the full fixed
          Account→Campaign→Ad set→Ad hierarchy), so hidden in Tree mode. */}
      {state.mode === 'flat' && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-slate-400">group by</span>
          {DIMENSION_ORDER.map((dim) => {
            const active = groupBy.includes(dim);
            return (
              <button
                key={dim}
                type="button"
                onClick={() => toggleDim(dim)}
                aria-pressed={active}
                className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                  active
                    ? 'border-kyck-600 bg-kyck-50 font-medium text-kyck-800'
                    : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {DIMENSION_LABEL[dim]}
              </button>
            );
          })}
          {query.isFetching && <span className="text-xs text-slate-400">updating…</span>}
        </div>
      )}

      {state.mode === 'tree' ? (
        <BreakdownTree
          filters={filters}
          visibleCols={visibleCols}
          sort={sort}
          onSortStat={onSortStat}
          onSortDim={onSortDim}
          sortArrow={sortArrow}
          money={money}
        />
      ) : query.isError ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          couldn't load the breakdown for this selection.
        </div>
      ) : (
        <>
          {sourceLevel && rollup.length > 0 && (
            <p className="mb-2 text-xs text-slate-400">{SOURCE_LEVEL_NOTE[sourceLevel]}</p>
          )}
          {/* ⚠️ The max-height is what makes the sticky header work — see the
              same note in GoogleAdsTree. `overflow-x-auto` alone makes this a
              scrollport in both axes, so a sticky header inside it has nothing
              to stick to and just scrolls off with the page. */}
          <div className="max-h-[75vh] overflow-auto rounded-md border border-slate-200 bg-white scroll-subtle">
            <table className="min-w-full border-collapse text-sm">
              {/* Nothing sticky sits above this table, so top-0 needs no offset. */}
              <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 shadow-[inset_0_-1px_0_#e2e8f0]">
                <tr>
                  {groupBy.map((dim) => (
                    <th key={dim} className="whitespace-nowrap px-3 py-3 font-medium">
                      <button
                        type="button"
                        onClick={onSortDim}
                        className="inline-flex items-center gap-1 hover:text-slate-900"
                      >
                        {DIMENSION_LABEL[dim]}
                        <span aria-hidden className="text-[10px]">
                          {sortArrow('name')}
                        </span>
                      </button>
                    </th>
                  ))}
                  {visibleCols.map((c) => {
                    const stat = isStatKey(c.key);
                    return (
                      <th
                        key={c.key}
                        className={`whitespace-nowrap px-3 py-3 font-medium ${stat ? 'text-right' : 'text-left'}`}
                      >
                        {stat ? (
                          <button
                            type="button"
                            onClick={() => onSortStat(c.key as BreakdownStatKey)}
                            className="inline-flex items-center gap-1 hover:text-slate-900"
                          >
                            {c.label}
                            <span aria-hidden className="text-[10px]">
                              {sortArrow(c.key as BreakdownStatKey)}
                            </span>
                          </button>
                        ) : (
                          c.label
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rollup.length === 0 ? (
                  <tr>
                    <td colSpan={totalColumns} className="px-4 py-8 text-center text-sm text-slate-500">
                      {query.isLoading ? (
                        'loading…'
                      ) : needsAdGrain ? (
                        <span>
                          no ad-level data for this selection — the selected account(s) may not be
                          backfilled at ad grain yet.
                        </span>
                      ) : (
                        'no data for this selection.'
                      )}
                    </td>
                  </tr>
                ) : (
                  rollup.map((row, i) => {
                    const rowTone =
                      row.isTopLevel && row.isSubtotal
                        ? 'bg-slate-50 font-semibold text-slate-800'
                        : row.isSubtotal
                          ? 'font-medium text-slate-700'
                          : 'text-slate-600';
                    return (
                      <tr key={i} className={`${separatorClass(row.separator)} ${rowTone} hover:bg-slate-50/70`}>
                        {groupBy.map((_, di) => (
                          <td key={di} className="whitespace-nowrap px-3 py-2 align-top">
                            {di === row.level ? renderBreakdownDimCell(row) : ''}
                          </td>
                        ))}
                        {visibleCols.map((c) =>
                          isStatKey(c.key) ? (
                            <td key={c.key} className="px-3 py-2 text-right align-top tabular-nums">
                              {renderBreakdownStatCell(row, c.key, money)}
                            </td>
                          ) : (
                            <td key={c.key} className="px-3 py-2 text-left align-top">
                              {renderBreakdownAttributeCell(row, c)}
                            </td>
                          ),
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {showStatusLegend && rollup.length > 0 && <StatusDotLegend />}
        </>
      )}
    </section>
  );
}
